/**
 * A unit stated in an offer REMARK that contradicts the line's UOM.
 *
 * The comparison's UOM is the RFQ's and is not editable — the whole column is ranked in it. A
 * supplier who priced against a different unit has nowhere to say so but the remark, and on this
 * bench two of nineteen remarks do exactly that (the measurements are in this module's test).
 * Missing them is expensive: "costing is based inches" on a metre line is out by a factor of 39.
 *
 * ---- Deliberately conservative ----
 *
 * This reads prose written by a supplier in a hurry, so a false positive costs a purchaser's
 * attention on a line that is fine — and attention spent on false alarms is attention not spent on
 * the real ones. Two rules keep it quiet:
 *
 *   - a fixed vocabulary of units, matched only as WHOLE WORDS;
 *   - nothing shorter than two characters, so the bare `m` in "OFFER U-HFA m FLAME RETARDANT
 *     ARMOURED POWER AND CONTROL CABLE" is not read as metres. That remark is real.
 *
 * It flags rather than blocks. A remark is not a fact about the quotation, and the purchaser is
 * the one who can read it and decide.
 */

import { sameUnit } from './customerUom'

/**
 * Units a supplier writes in prose — measurement and packaging both.
 *
 * Two characters minimum, on purpose (see the header). `m`, `g` and `l` are therefore absent, and
 * that is a deliberate loss: their spelled-out and abbreviated forms are here instead.
 */
const REMARK_UNITS = [
  // length
  'inch', 'inches', 'in.', 'ft', 'feet', 'foot', 'mtr', 'mtrs', 'meter', 'meters', 'metre', 'metres',
  'cm', 'mm', 'yard', 'yards',
  // mass
  'kg', 'kgs', 'gm', 'gms', 'gram', 'grams', 'ton', 'tons', 'lb', 'lbs',
  // volume
  'ltr', 'ltrs', 'litre', 'litres', 'liter', 'liters', 'ml', 'gal', 'gallon', 'gallons',
  // count / packaging
  'pc', 'pcs', 'piece', 'pieces', 'nos', 'no.', 'ea', 'each',
  'doz', 'dozen', 'pkt', 'pkts', 'packet', 'packets', 'pack', 'packs',
  'box', 'boxes', 'ctn', 'carton', 'cartons', 'case', 'cases',
  'bag', 'bags', 'roll', 'rolls', 'set', 'sets', 'pair', 'pairs',
  'tin', 'tins', 'can', 'cans', 'btl', 'bottle', 'bottles', 'drum', 'drums',
  'sheet', 'sheets', 'reel', 'reels', 'coil', 'coils', 'tube', 'tubes',
]

/**
 * The first unit named in `remark` that is not the line's own, or `null`.
 *
 * Comparison goes through `customerUom`'s `sameUnit`, so case, plurals and same-word
 * abbreviations (`PKT` ≡ `PACKET`, `CTN` ≡ `CARTON`) count as agreement rather than a discrepancy
 * of one — a warning nobody could act on is worse than none.
 */
export function remarkUomConflict(remark: string | null | undefined, lineUom: string): string | null {
  const text = (remark ?? '').trim()
  const uom = (lineUom ?? '').trim()
  if (text === '' || uom === '') return null

  /*
    A unit only counts when it stands as its OWN word.

    Splitting on non-letters alone was not enough: the live cable remark contains `1CX120MM`, a
    120 mm² cross-section, which yielded `MM` and flagged a line that was perfectly fine. A unit
    glued to digits describes the GOODS — `120MM`, `500ML`, `2.5KG` — while a free-standing one
    states how they are priced.

    Hence the alphanumeric boundaries on both sides: letters and digits both disqualify, so
    `120MM` and `MMX` are out while `per MM` and `PCS/CTN` are in.
  */
  for (const unit of REMARK_UNITS) {
    const escaped = unit.replace(/\./g, '\\.')
    if (!new RegExp(`(?<![A-Za-z0-9])${escaped}(?![A-Za-z0-9])`, 'i').test(text)) continue
    // Named the line's own unit — agreement, not a conflict.
    if (sameUnit(unit, uom)) continue
    // Reported as the supplier wrote it, not as the vocabulary spells it.
    const written = new RegExp(`(?<![A-Za-z0-9])(${escaped})(?![A-Za-z0-9])`, 'i').exec(text)
    return written?.[1] ?? unit
  }

  return null
}
